import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_LIFTPLAN } from '../../../lib/constants';
import { TLiftPlan } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { renderOptions } from '../../../lib/util/render';
import {
  LIFTPLAN_EQUIPMENT_STATUS_ENUM,
  LIFTPLAN_STATUS_ENUM,
  LIFTPLAN_STRUCTURAL_ASSESSMENT_ENUM,
  LIFTPLAN_TYPE_ENUM,
} from '../../../lib/constants/dbConstants';
import {
  LiftplanEquipmentStatusEnum,
  LiftplanStatusEnum,
  LiftplanStructuralAssessmentEnum,
  LiftplanTypeEnum,
} from '../../../graph-queries/__generated__/graphql';

const LiftplanForm: FC<{
  defaultValues?: TLiftPlan;
  onSubmit: (liftPlan: TLiftPlan) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      type: LiftplanTypeEnum;
      equipmentStatus: LiftplanEquipmentStatusEnum;
      structuralAssessment: LiftplanStructuralAssessmentEnum;
      status: LiftplanStatusEnum;
    };

    const liftPlan: TLiftPlan = {
      ...EMPTY_LIFTPLAN,
      ...inputData,
      assetId: asset?.assetId || -1,
      erectHours: parseInt(`${inputData.erectHours}`),
      deconstructHours: parseInt(`${inputData.deconstructHours}`),
      oversideCover: inputData.oversideCover === 'Yes',
    };
    if (defaultValues?.liftPlanId) liftPlan.liftPlanId = defaultValues.liftPlanId;

    onSubmit(liftPlan)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-number">Lift Plan #</Label>
          <Input
            type="text"
            name="planNumber"
            id="liftplan-number"
            required
            defaultValue={defaultValues?.planNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="liftplan-description">Lift Plan Description</Label>
          <Input
            type="text"
            name="description"
            id="liftplan-description"
            required
            defaultValue={defaultValues?.description || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-type">Type</Label>
          <Input
            type="select"
            name="type"
            id="scaffold-type"
            defaultValue={defaultValues?.type || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Select option
            </option>
            {renderOptions(LIFTPLAN_TYPE_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-equipment-status">Lifting Equipment</Label>
          <Input
            type="select"
            name="equipmentStatus"
            id="scaffold-equipment-status"
            defaultValue={defaultValues?.equipmentStatus || 'Required'}
          >
            <option disabled hidden value="" key="_placeholder_">
              Select option
            </option>

            {renderOptions(LIFTPLAN_EQUIPMENT_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-structural-assessment">Structural Assessment</Label>
          <Input
            type="select"
            name="structuralAssessment"
            id="scaffold-structural-assessment"
            defaultValue={defaultValues?.structuralAssessment || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Select option
            </option>

            {renderOptions(LIFTPLAN_STRUCTURAL_ASSESSMENT_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-status">Status</Label>

          <Input
            type="select"
            name="status"
            id="scaffold-status"
            defaultValue={defaultValues?.status || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Select option
            </option>

            {renderOptions(LIFTPLAN_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-safe-working">Safe Working</Label>
          <Input
            type="text"
            name="safeWorking"
            id="scaffold-safe-working"
            defaultValue={defaultValues?.safeWorking || ''}
          />
        </FormGroup>
      </div>

      <div className="col-md-6">
        <FormGroup>
          <Label for="liftplan-overside-cover">Overside Cover</Label>
          <Input
            type="select"
            name="oversideCover"
            id="liftplan-overside-cover"
            defaultValue={defaultValues?.oversideCover === true ? 'Yes' : 'No'} // Explicit boolean comparison added to assert distinction with form field which is a string
          >
            <option disabled hidden value="" key="_placeholder_">
              Select option
            </option>
            <option>Yes</option>
            <option>No</option>
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-erect">Erect Hours</Label>
          <Input
            type="text"
            name="erectHours"
            id="scaffold-erect"
            defaultValue={defaultValues?.erectHours ?? ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-4">
        <FormGroup>
          <Label for="scaffold-deconstruct">Deconstruct Hours</Label>
          <Input
            type="text"
            name="deconstructHours"
            id="scaffold-deconstruct"
            defaultValue={defaultValues?.deconstructHours ?? ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="liftPlan-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="liftPlan-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new Lift Plan
          </Button>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default LiftplanForm;
